import { ArrowLeft, ChevronLeft, ChevronRight, Minus, Plus, RotateCcw } from "lucide-react";

/**
 * On-screen equivalents for every keyboard gesture, at 44px so they are usable with a
 * thumb. The map is not allowed to have a capability that is mouse- or key-only.
 */

const BUTTON =
  "grid h-10 w-10 place-items-center border border-border bg-card text-foreground transition-surface hover:bg-secondary disabled:pointer-events-none disabled:opacity-40 sm:h-11 sm:w-11";

export function MapZoomControls({
  onZoomIn,
  onZoomOut,
  onReset,
  canZoomIn,
  canZoomOut,
  atDefault,
}: {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onReset: () => void;
  canZoomIn: boolean;
  canZoomOut: boolean;
  atDefault: boolean;
}) {
  return (
    // On a phone the map is ~220px tall and a vertical stack at the bottom right sat on
    // the whole East Coast, the part of the map most readers came for. There the row
    // goes bottom-left, over the Alaska and Hawaii insets.
    <div className="absolute bottom-2 left-2 flex -space-x-px sm:bottom-3 sm:left-auto sm:right-3 sm:flex-col sm:space-x-0 sm:-space-y-px">
      <button
        type="button"
        className={BUTTON}
        onClick={onZoomIn}
        disabled={!canZoomIn}
        aria-label="Zoom in"
      >
        <Plus className="h-5 w-5" aria-hidden />
      </button>
      <button
        type="button"
        className={BUTTON}
        onClick={onZoomOut}
        disabled={!canZoomOut}
        aria-label="Zoom out"
      >
        <Minus className="h-5 w-5" aria-hidden />
      </button>
      <button
        type="button"
        className={BUTTON}
        onClick={onReset}
        disabled={atDefault}
        aria-label="Reset the map to the whole United States"
      >
        <RotateCcw className="h-5 w-5" aria-hidden />
      </button>
    </div>
  );
}

export function MapBackControl({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="absolute left-3 top-3 inline-flex h-10 items-center gap-1.5 rounded-sm border border-border bg-card px-3 text-sm font-semibold text-foreground transition-surface hover:bg-secondary"
    >
      <ArrowLeft className="h-4 w-4" aria-hidden />
      {label}
    </button>
  );
}

export function MapStepControls({
  onPrevious,
  onNext,
  label,
}: {
  onPrevious: () => void;
  onNext: () => void;
  label: string;
}) {
  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={onPrevious}
        className="inline-flex h-11 items-center gap-1 rounded-sm border border-border bg-card px-3 text-sm font-medium text-foreground transition-surface hover:border-foreground/40"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden /> Previous
      </button>
      <button
        type="button"
        onClick={onNext}
        className="inline-flex h-11 items-center gap-1 rounded-sm border border-border bg-card px-3 text-sm font-medium text-foreground transition-surface hover:border-foreground/40"
      >
        Next <ChevronRight className="h-4 w-4" aria-hidden />
      </button>
      <span className="text-xs text-muted-foreground">{label}</span>
    </div>
  );
}
